"use client"

import { useEffect, useState } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { TrendingUp, TrendingDown, ExternalLink } from "lucide-react"
import { cn } from "@/lib/utils"

interface TopStock {
  symbol: string
  name: string
  price: number
  change: number
  changePercent: number
  volume?: number
  high?: number
  low?: number
  open?: number
}

export function TopStocksSection() {
  const [stocks, setStocks] = useState<TopStock[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchTopStocks() {
      try {
        const response = await fetch("/api/top-stocks")
        const data = await response.json()
        if (data.stocks && Array.isArray(data.stocks)) {
          setStocks(data.stocks)
        } else {
          // Fallback to mock data
          setStocks([
            { symbol: "AAPL", name: "Apple Inc.", price: 175.43, change: 2.34, changePercent: 1.35, volume: 45234567 },
            { symbol: "MSFT", name: "Microsoft", price: 378.85, change: -1.23, changePercent: -0.32, volume: 23456789 },
            { symbol: "GOOGL", name: "Alphabet", price: 142.56, change: 3.45, changePercent: 2.48, volume: 34567890 },
            { symbol: "NVDA", name: "NVIDIA", price: 485.23, change: 12.45, changePercent: 2.63, volume: 56789012 },
            { symbol: "META", name: "Meta Platforms", price: 312.45, change: 5.67, changePercent: 1.85, volume: 23456789 },
            { symbol: "AMZN", name: "Amazon", price: 145.32, change: -2.34, changePercent: -1.59, volume: 45678901 },
            { symbol: "TSLA", name: "Tesla", price: 248.50, change: -5.67, changePercent: -2.23, volume: 67890123 },
            { symbol: "NFLX", name: "Netflix", price: 425.67, change: 8.90, changePercent: 2.14, volume: 12345678 },
          ])
        }
      } catch (error) {
        console.error("Error fetching top stocks:", error)
        // Use fallback mock data
        setStocks([
          { symbol: "AAPL", name: "Apple Inc.", price: 175.43, change: 2.34, changePercent: 1.35 },
          { symbol: "MSFT", name: "Microsoft", price: 378.85, change: -1.23, changePercent: -0.32 },
          { symbol: "GOOGL", name: "Alphabet", price: 142.56, change: 3.45, changePercent: 2.48 },
        ])
      } finally {
        setLoading(false)
      }
    }

    fetchTopStocks()
  }, [])

  if (loading) {
    return (
      <section className="w-full py-20 md:py-28 px-4 md:px-8 lg:px-12 bg-black border-t border-gray-800/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center text-gray-400">Loading top stocks...</div>
        </div>
      </section>
    )
  }

  return (
    <section className="w-full py-20 md:py-28 px-4 md:px-8 lg:px-12 bg-black border-t border-gray-800/50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-light tracking-[0.15em] text-white uppercase mb-4">
            Top Stocks Today
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Track the most active and trending stocks from the market
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {stocks.map((stock) => {
            const isPositive = stock.changePercent >= 0
            return (
              <Card
                key={stock.symbol}
                className="bg-gray-900/50 backdrop-blur-sm border-gray-800/50 shadow-xl hover:shadow-2xl hover:border-green-500/30 transition-all duration-300 group"
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-white">{stock.symbol}</h3>
                        <a
                          href={`https://finance.yahoo.com/quote/${stock.symbol}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="h-3 w-3 text-gray-500 hover:text-green-500" />
                        </a>
                      </div>
                      <p className="text-xs text-gray-400 truncate">{stock.name}</p>
                    </div>
                    <div className={cn(
                      "flex items-center gap-1",
                      isPositive ? "text-green-500" : "text-red-500"
                    )}>
                      {isPositive ? (
                        <TrendingUp className="h-4 w-4" />
                      ) : (
                        <TrendingDown className="h-4 w-4" />
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-baseline justify-between">
                      <span className="text-2xl font-bold text-white">
                        ${stock.price.toFixed(2)}
                      </span>
                      <span className={cn(
                        "text-sm font-semibold",
                        isPositive ? "text-green-500" : "text-red-500"
                      )}>
                        {isPositive ? '+' : ''}{stock.changePercent.toFixed(2)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>
                        {isPositive ? '+' : ''}${Math.abs(stock.change).toFixed(2)}
                      </span>
                      {stock.volume && (
                        <span>
                          Vol: {(stock.volume / 1000000).toFixed(1)}M
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Mini bar chart indicator */}
                  <div className="mt-4 h-1 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className={cn(
                        "h-full transition-all duration-500",
                        isPositive ? "bg-green-500" : "bg-red-500"
                      )}
                      style={{
                        width: `${Math.min(100, Math.abs(stock.changePercent) * 10)}%`,
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>

        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            Data sourced from Yahoo Finance • Updated in real-time
          </p>
        </div>
      </div>
    </section>
  )
}

